import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useArtistAuth } from '@/context/ArtistAuthContext';
import { useCollaboratorAuth } from '@/context/CollaboratorAuthContext';
import SEO from '@/components/SEO';
import usePageContent from '@/lib/usePageContent';
import RegisteredArtistCard from '@/components/RegisteredArtistCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const publicEventSourceLabel = (source = '') => {
  const clean = String(source || '').toLowerCase();
  if (clean.includes('serpapi') || clean.includes('google')) return 'Live Event';
  if (clean.includes('eventbrite')) return 'Ticketed Event';
  return 'Event Lead';
};

const socialSharePlatforms = [
  { key: 'Instagram', label: 'Instagram', icon: 'IG', url: 'https://www.instagram.com/', color: 'from-[#F97316] to-[#C026D3]' },
  { key: 'Facebook', label: 'Facebook', icon: 'f', url: 'https://www.facebook.com/', color: 'from-[#2563EB] to-[#1D4ED8]' },
  { key: 'YouTube', label: 'YouTube', icon: 'YT', url: 'https://studio.youtube.com/', color: 'from-[#EF4444] to-[#B91C1C]' },
  { key: 'X', label: 'X', icon: 'X', url: 'https://x.com/compose/post', color: 'from-slate-950 to-slate-700' },
  { key: 'WhatsApp', label: 'WhatsApp', icon: 'WA', url: 'https://web.whatsapp.com/', color: 'from-[#16A34A] to-[#047857]' },
];

const extractResultSection = (text = '', heading = '') => {
  const escaped = heading.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = String(text || '').match(new RegExp(`${escaped}:\\s*([\\s\\S]*?)(?=\\n(?:Instagram|Facebook|YouTube|X|WhatsApp):|$)`, 'i'));
  return (match?.[1] || text || '').trim();
};

const copyTextToClipboard = async (text = '') => {
  await navigator.clipboard.writeText(String(text || ''));
};

const shareSocialText = async (platform, text = '') => {
  const cleanText = String(text || '').trim();
  if (navigator.share && cleanText) {
    await navigator.share({
      title: `KalaManch ${platform.label} post`,
      text: cleanText,
    });
    return;
  }
  await copyTextToClipboard(cleanText);
  window.open(platform.key === 'X' ? `https://x.com/compose/post?text=${encodeURIComponent(cleanText.slice(0, 260))}` : platform.url, '_blank', 'noopener,noreferrer');
};

const socialCardText = (result = '', platformKey = '') => {
  const sectionText = extractResultSection(result, platformKey);
  if (sectionText && sectionText !== result) return sectionText;
  return sectionText || 'Generate a Social Post Pack to prepare this platform copy.';
};

const cleanAiLine = (line = '') => String(line || '')
  .replace(/^#{1,6}\s*/, '')
  .replace(/\*\*/g, '')
  .replace(/^[-*]\s*/, '')
  .trim();

const ResultBlock = ({ text }) => {
  const lines = String(text || '').split('\n');
  return (
    <div className="mt-3 space-y-2">
      {lines.map((line, index) => {
        const raw = String(line || '').trim();
        if (!raw) return <div key={`space-${index}`} className="h-2" />;
        const cleaned = cleanAiLine(raw);
        const isBullet = /^[-*]\s+/.test(raw) || /^\d+[\).]\s+/.test(raw);
        const isHeading = /^#{1,6}\s+/.test(raw) || /^\*\*.+\*\*:?\s*$/.test(raw) || (cleaned.endsWith(':') && cleaned.length < 80);
        if (isHeading) {
          return (
            <h4 key={`${cleaned}-${index}`} className="pt-3 text-base font-black text-slate-950">
              {cleaned.replace(/:$/, '')}
            </h4>
          );
        }
        if (isBullet) {
          return (
            <div key={`${cleaned}-${index}`} className="flex gap-3 rounded-2xl bg-white/70 px-3 py-2">
              <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-gradient-to-br from-[#FF6F00] to-[#4A148C]" />
              <p className="leading-7 text-slate-700">{cleaned.replace(/^\d+[\).]\s*/, '')}</p>
            </div>
          );
        }
        return (
          <p key={`${cleaned}-${index}`} className="leading-7 text-slate-700">
            {cleaned}
          </p>
        );
      })}
    </div>
  );
};

const tools = [
  {
    id: 'bio',
    title: 'Artist Bio',
    subtitle: 'Professional bio, short bio, Hindi + English',
    endpoint: 'bio',
    button: 'Generate Bio',
    placeholder: 'Art form, style, experience, city, achievements, and what makes the artist unique',
    nameLabel: 'Artist name',
    artFormLabel: 'Art form',
    purpose: 'Bio',
  },
  {
    id: 'caption',
    title: 'Caption',
    subtitle: 'Instagram caption, hashtags, emotional and viral versions',
    endpoint: 'caption',
    button: 'Generate Caption',
    placeholder: 'Describe the artwork, performance, reel, event, mood, or story behind the post',
    nameLabel: 'Artist name',
    artFormLabel: 'Art form',
    purpose: 'Caption',
  },
  {
    id: 'social-post-pack',
    title: 'Social Post Pack',
    subtitle: 'Ready-to-share posts for Instagram, Facebook, YouTube, X, and WhatsApp',
    endpoint: 'social-post-pack',
    button: 'Create Post Pack',
    placeholder: 'Describe the artwork, reel, event, release, announcement, portfolio update, audience, language, and any link or CTA you want included',
    nameLabel: 'Artist name',
    artFormLabel: 'Art form',
    purpose: 'Social post pack',
  },
  {
    id: 'audience-predictor',
    title: 'Audience Predictor',
    subtitle: 'Age appeal, city fit, emotions, virality, timing, and best platform',
    endpoint: 'audience-predictor',
    button: 'Predict Audience',
    placeholder: 'Describe or paste a link/context for your song, painting, reel, poetry, performance, audience goal, language, city, mood, and where you plan to post.',
    nameLabel: 'Artist name',
    artFormLabel: 'Content type / art form',
    purpose: 'Audience prediction',
  },
  {
    id: 'reel-script',
    title: 'Reel Script',
    subtitle: '30-second hook, middle, CTA, Hinglish option',
    endpoint: 'reel-script',
    button: 'Generate Reel Script',
    placeholder: 'Type of reel: poetry, dance, painting, singing, behind-the-scenes, event announcement',
    nameLabel: 'Artist name',
    artFormLabel: 'Art form',
    purpose: 'Reel script',
  },
  {
    id: 'artist-statement',
    title: 'Artist Statement',
    subtitle: 'For exhibitions, profile pages, proposals, and media kits',
    endpoint: 'artist-statement',
    button: 'Generate Statement',
    placeholder: 'Artist vision, themes, process, materials, influences, and message for viewers',
    nameLabel: 'Artist name',
    artFormLabel: 'Art form',
    purpose: 'Artist statement',
  },
  {
    id: 'portfolio-review',
    title: 'AI Portfolio Review',
    subtitle: 'Strengths, improvement areas, audience fit, branding, and pricing tips',
    endpoint: 'portfolio-review',
    button: 'Review Portfolio',
    placeholder: 'Paste portfolio links or describe artwork/song/video/reel, profile bio, audience, city, current pricing, goals, and what feedback you want. Phase 1 reviews the details and public links you provide.',
    nameLabel: 'Artist name',
    artFormLabel: 'Art form',
    purpose: 'Portfolio review',
  },
  {
    id: 'hashtags',
    title: 'Hashtag Set',
    subtitle: 'Clean discovery hashtags for Instagram, YouTube, and events',
    endpoint: 'hashtags',
    button: 'Generate Hashtags',
    placeholder: 'Describe the artwork, location, art form, occasion, audience, and platform',
    nameLabel: 'Artist name',
    artFormLabel: 'Art form',
    purpose: 'Hashtags',
  },
  {
    id: 'event-announcement',
    title: 'Event Announcement',
    subtitle: 'Launch post, WhatsApp invite, short press note, and CTA',
    endpoint: 'event-announcement',
    button: 'Generate Announcement',
    placeholder: 'Event name, date, venue/city, artists, ticket/contact details, and mood',
    nameLabel: 'Organizer / artist name',
    artFormLabel: 'Event type',
    purpose: 'Event invite',
  },
  {
    id: 'poster-copy',
    title: 'Poster Copy',
    subtitle: 'Poster text, headline options, layout notes, and social caption',
    endpoint: 'poster-copy',
    button: 'Generate Poster Copy',
    placeholder: 'What the poster is for, key names, date, location, contact, and visual style',
    nameLabel: 'Organizer / artist name',
    artFormLabel: 'Poster / event type',
    purpose: 'Poster copy',
  },
  {
    id: 'event-finder',
    title: 'Find Event Artists',
    subtitle: 'Find KalaManch artist roles, matches, filters, and contact message',
    endpoint: 'event-finder',
    button: 'Find Event Artists',
    placeholder: 'Event type, city/state, audience size, required vibe, budget range, and preferred artist types',
    nameLabel: 'Organizer name',
    artFormLabel: 'Event type',
    purpose: 'Find artists',
  },
  {
    id: 'events-near-me',
    title: 'Find Events Near Me',
    subtitle: 'Find public event leads where artists can perform or approach',
    endpoint: 'events-near-me',
    button: 'Find Events',
    placeholder: 'Date range, event style, preferred audience, and keywords. Example: this month, music festivals, cultural programs, college events',
    nameLabel: 'Artist type / art form',
    artFormLabel: 'City / state',
    purpose: 'Find events',
  },
  {
    id: 'event-manager',
    title: 'AI Event Manager',
    subtitle: 'Plan lineup, timeline, stage sequence, checklist, and cost assumptions',
    endpoint: 'event-manager',
    button: 'Plan My Event',
    placeholder: 'Event goal, venue/city, date, duration, expected audience, artists needed, sound/stage needs, and budget range',
    nameLabel: 'Organizer name',
    artFormLabel: 'Event type',
    purpose: 'Plan my event',
  },
  {
    id: 'creative-team',
    title: 'Creative Teams',
    subtitle: 'Build reusable teams for weddings, films, launches, and cultural programs',
    endpoint: 'creative-team',
    button: 'Build Team',
    placeholder: 'Project type, city, roles needed, audience, budget range, timeline, responsibilities, and the kind of artists or collaborators you want in the team',
    nameLabel: 'Organizer name',
    artFormLabel: 'Team / project type',
    purpose: 'Build team',
  },
];

const toolGroups = [
  { title: 'Profile', ids: ['bio', 'artist-statement', 'portfolio-review'] },
  { title: 'Social Growth', ids: ['caption', 'social-post-pack', 'audience-predictor', 'hashtags', 'reel-script'] },
  { title: 'Events', ids: ['event-announcement', 'poster-copy', 'event-finder', 'events-near-me', 'event-manager', 'creative-team'] },
];

const languageOptions = [
  'English + Hindi',
  'Hindi',
  'Hinglish',
  'Urdu',
  'Punjabi',
  'Bhojpuri',
  'Bengali',
  'Marathi',
  'Tamil',
  'Telugu',
  'Gujarati',
  'Kannada',
  'Malayalam',
  'Odia',
  'Assamese',
];

const purposeOptions = [
  'Caption',
  'Social post pack',
  'Audience prediction',
  'Bio',
  'Reel script',
  'Artist statement',
  'Poster copy',
  'Event invite',
  'Poetry formatting',
  'Stage announcement',
  'Hashtags',
  'Press note',
  'Find artists',
  'Find events',
  'Plan my event',
  'Build team',
  'Portfolio review',
];

const EVENT_ARTIST_TERMS = [
  'singer', 'vocalist', 'bhajan', 'qawwali', 'sufi', 'folk', 'band', 'musician',
  'tabla', 'dholak', 'guitar', 'instrumentalist', 'composer', 'lyricist', 'poet',
  'shayari', 'anchor', 'emcee', 'host', 'dancer', 'choreographer', 'actor',
  'comedian', 'speaker', 'dj', 'photographer', 'videographer', 'painter',
];

const tokenizeMatchText = (...values) => {
  const text = values.filter(Boolean).join(' ').toLowerCase();
  const words = text
    .replace(/[^\p{L}\p{N}\s-]/gu, ' ')
    .split(/\s+/)
    .map((word) => word.trim())
    .filter((word) => word.length >= 3);
  const roleTerms = EVENT_ARTIST_TERMS.filter((term) => text.includes(term));
  return Array.from(new Set([...roleTerms, ...words])).slice(0, 30);
};

const rankEventArtist = (artist = {}, tokens = [], cityText = '') => {
  const searchable = [
    artist.name,
    artist.category,
    artist.subcategory,
    artist.city,
    artist.state,
    artist.bio,
    artist.native_bio,
    artist.native_language,
    artist.native_language_badge,
    artist.smart_fit,
  ].filter(Boolean).join(' ').toLowerCase();
  const cleanCity = cityText.trim().toLowerCase();
  let score = 0;
  tokens.forEach((token) => {
    if (searchable.includes(token)) score += EVENT_ARTIST_TERMS.includes(token) ? 5 : 2;
  });
  if (cleanCity && [artist.city, artist.state].filter(Boolean).some((value) => String(value).toLowerCase().includes(cleanCity))) score += 8;
  if (artist.allow_collaborator_enquiries !== false) score += 3;
  if (artist.is_verified) score += 3;
  if (artist.is_featured) score += 2;
  return score;
};

const getEventArtistMatches = (artists = [], { artForm, details, result }) => {
  const tokens = tokenizeMatchText(artForm, details, result);
  if (tokens.length === 0) return [];
  return artists
    .map((artist) => ({ ...artist, match_score: rankEventArtist(artist, tokens, details) }))
    .filter((artist) => artist.match_score > 0)
    .sort((a, b) => b.match_score - a.match_score)
    .slice(0, 6);
};

export default function AiStudio() {
  const managedContent = usePageContent('ai_studio');
  const { artist, loading: artistLoading } = useArtistAuth();
  const { collaborator, loading: collaboratorLoading } = useCollaboratorAuth();
  const [selectedTool, setSelectedTool] = useState(tools[0]);
  const [artistName, setArtistName] = useState('');
  const [artForm, setArtForm] = useState('');
  const [tone, setTone] = useState('Professional');
  const [language, setLanguage] = useState('English + Hindi');
  const [purpose, setPurpose] = useState(tools[0].purpose);
  const [selectedGroup, setSelectedGroup] = useState(toolGroups[0].title);
  const [details, setDetails] = useState('');
  const [results, setResults] = useState({});
  const [translatedResults, setTranslatedResults] = useState({});
  const [translationLanguage, setTranslationLanguage] = useState('Hindi');
  const [opportunityResults, setOpportunityResults] = useState({});
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState('');
  const [error, setError] = useState('');
  const [copiedTool, setCopiedTool] = useState('');
  const [usage, setUsage] = useState(null);
  const [toolSettings, setToolSettings] = useState({});
  const formRef = useRef(null);

  const authLoading = artistLoading || collaboratorLoading;
  const isAuthenticated = Boolean(artist || collaborator);
  const activeUser = artist || collaborator;
  const isAdminVerifiedUser = Boolean(activeUser?.is_verified || activeUser?.is_approved || activeUser?.verified);
  const aiVerificationMessage = 'AI Studio is available after KalaManch admin verifies your artist/collaborator profile. Please complete your profile and wait for approval.';
  const currentResult = results[selectedTool.id] || '';
  const currentTranslation = translatedResults[selectedTool.id];
  const displayedResult = currentTranslation?.text || currentResult;
  const selectedToolSetting = toolSettings[selectedTool.id] || {};
  const selectedToolEnabled = selectedToolSetting.enabled !== false;
  const selectedToolCreditCost = Number(selectedToolSetting.credit_cost || 1);
  const selectedToolAccess = selectedToolSetting.access_tier || 'free_beta';
  const visibleTools = useMemo(
    () => tools.filter((tool) => toolGroups.find((group) => group.title === selectedGroup)?.ids.includes(tool.id)),
    [selectedGroup]
  );
  const matchedArtists = useMemo(
    () => selectedTool.id === 'event-finder' && currentResult
      ? getEventArtistMatches(artists, { artForm, details, result: currentResult })
      : [],
    [artForm, artists, currentResult, details, selectedTool.id]
  );
  const dailyRemaining = usage?.daily_remaining ?? 0;
  const hasUsage = !usage || (usage.enabled && dailyRemaining >= selectedToolCreditCost && usage.platform_daily_remaining >= selectedToolCreditCost);

  useEffect(() => {
    fetch(`${API}/ai/tools`)
      .then(async (response) => {
        const data = await response.json();
        if (!response.ok) throw new Error(data.detail || 'Could not load AI Studio tools.');
        const settingsMap = (data.tools || []).reduce((map, tool) => ({ ...map, [tool.id]: tool }), {});
        setToolSettings(settingsMap);
      })
      .catch(() => setToolSettings({}));
  }, []);

  useEffect(() => {
    if (!Object.keys(toolSettings).length || selectedToolEnabled) return;
    const replacement = visibleTools.find((tool) => toolSettings[tool.id]?.enabled !== false) || tools.find((tool) => toolSettings[tool.id]?.enabled !== false);
    if (replacement) chooseTool(replacement);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedToolEnabled, toolSettings, visibleTools]);

  useEffect(() => {
    if (!isAuthenticated || !isAdminVerifiedUser) {
      setUsage(null);
      return;
    }

    fetch(`${API}/ai/usage`, {
      credentials: 'include',
    })
      .then(async (response) => {
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.detail || 'Could not load AI Studio usage.');
        }
        setUsage(data);
      })
      .catch((err) => {
        setError(err.message || 'Could not load AI Studio usage.');
      });
  }, [isAuthenticated, isAdminVerifiedUser]);

  useEffect(() => {
    fetch(`${API}/artists/all`)
      .then(async (response) => {
        const data = await response.json();
        if (!response.ok) throw new Error(data.detail || 'Could not load artists.');
        setArtists(data.artists || []);
      })
      .catch(() => setArtists([]));
  }, []);

  const generate = async () => {
    if (!isAuthenticated) {
      setError('Please login as a registered artist or collaborator to use AI Studio.');
      return;
    }
    if (!isAdminVerifiedUser) {
      setError(aiVerificationMessage);
      return;
    }
    if (usage && usage.daily_remaining <= 0) {
      setError("Today's free AI Studio limit is finished. Please try again tomorrow.");
      return;
    }
    if (!selectedToolEnabled) {
      setError(`${selectedTool.title} is temporarily paused by KalaManch admin.`);
      return;
    }
    if (usage && usage.daily_remaining < selectedToolCreditCost) {
      setError(`${selectedTool.title} needs ${selectedToolCreditCost} credit${selectedToolCreditCost > 1 ? 's' : ''}. Today's free AI Studio limit is finished.`);
      return;
    }
    if (usage && usage.platform_daily_remaining < selectedToolCreditCost) {
      setError("KalaManch AI Studio has reached today's free launch limit. Please try again tomorrow.");
      return;
    }
    if (usage && !usage.enabled) {
      setError('AI Studio is temporarily paused.');
      return;
    }

    setLoading(selectedTool.id);
    setError('');
    setCopiedTool('');

    try {
      const response = await fetch(`${API}/ai/${selectedTool.endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          artistName,
          artForm,
          tone,
          language,
          details: `Purpose: ${purpose}\n${details}`,
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'AI Studio could not generate this content.');
      }

      setResults((current) => ({
        ...current,
        [selectedTool.id]: data.result || '',
      }));
      setTranslatedResults((current) => {
        const next = { ...current };
        delete next[selectedTool.id];
        return next;
      });
      if (data.opportunities) {
        setOpportunityResults((current) => ({
          ...current,
          [selectedTool.id]: data.opportunities,
        }));
      }
      if (data.usage) {
        setUsage(data.usage);
      }
    } catch (err) {
      setError(err.message || 'AI Studio could not complete the request.');
    } finally {
      setLoading('');
    }
  };

  const copyResult = async () => {
    if (!displayedResult) return;
    await navigator.clipboard.writeText(displayedResult);
    setCopiedTool(selectedTool.id);
  };

  const chooseTool = (tool) => {
    setSelectedTool(tool);
    setPurpose(tool.purpose || 'Caption');
    setDetails('');
    setError('');
    setCopiedTool('');
    setResults((current) => {
      const next = { ...current };
      delete next[tool.id];
      return next;
    });
    setOpportunityResults((current) => {
      const next = { ...current };
      delete next[tool.id];
      return next;
    });
    setTranslatedResults((current) => {
      const next = { ...current };
      delete next[tool.id];
      return next;
    });
    window.setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 60);
  };

  const translateResult = async () => {
    if (!currentResult) return;
    if (!isAuthenticated) {
      setError('Please login as a registered artist or collaborator to translate AI Studio results.');
      return;
    }
    if (!isAdminVerifiedUser) {
      setError(aiVerificationMessage);
      return;
    }
    setLoading(`${selectedTool.id}-translate`);
    setError('');
    setCopiedTool('');
    try {
      const response = await fetch(`${API}/ai/translate-result`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          sourceText: currentResult,
          targetLanguage: translationLanguage,
          toolName: selectedTool.title,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'AI Studio could not translate this result.');
      }
      setTranslatedResults((current) => ({
        ...current,
        [selectedTool.id]: {
          language: translationLanguage,
          text: data.result || '',
        },
      }));
      if (data.usage) {
        setUsage(data.usage);
      }
    } catch (err) {
      setError(err.message || 'AI Studio could not translate this result.');
    } finally {
      setLoading('');
    }
  };

  const currentOpportunities = opportunityResults[selectedTool.id] || null;
  const showRawResult = displayedResult && !['events-near-me', 'social-post-pack'].includes(selectedTool.id);

  return (
    <main className="min-h-screen bg-[#f7f9fc] pt-28 pb-16">
      <SEO
        title="KalaManch AI Studio"
        description="Generate artist bios, captions, hashtags, reel scripts, event announcements, poster copy, and artist statements with KalaManch AI Studio."
        keywords="KalaManch AI Studio, AI tools for artists, artist bio generator, caption generator, reel script generator"
      />
      <section className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-6 lg:grid-cols-[0.68fr_1.32fr] lg:items-start">
          <div className="rounded-3xl border border-orange-100 bg-white p-5 shadow-xl sm:p-6 lg:sticky lg:top-28">
            <p className="text-sm font-bold uppercase tracking-[0.24em] text-[#FF6F00]">Phase 1</p>
            <h1 className="mt-3 text-3xl font-bold leading-tight text-gray-950 sm:text-4xl">{managedContent.hero_title || 'KalaManch AI Studio'}</h1>
            <p className="mt-3 text-sm leading-6 text-gray-600">
              {managedContent.hero_subtitle || 'Fast writing tools for artists: bios, captions, reel scripts, and artist statements.'}
            </p>

            <div className="mt-5 rounded-2xl border border-orange-100 bg-orange-50 px-4 py-3 text-sm font-semibold text-gray-700">
              {isAuthenticated ? (
                !isAdminVerifiedUser ? (
                  <span className="text-amber-800">{aiVerificationMessage}</span>
                ) :
                usage ? (
                  <>
                    Your free uses left today: <span className="text-[#FF6F00]">{usage.daily_remaining}</span> / {usage.daily_limit}
                    <span className="mt-1 block text-xs text-gray-500">
                      KalaManch launch pool left today: {usage.platform_daily_remaining} / {usage.platform_daily_limit}
                    </span>
                  </>
                ) : (
                  'Checking your free AI Studio limit...'
                )
              ) : (
                'Login as an artist or collaborator to generate with AI Studio.'
              )}
            </div>

            <div className="mt-4 rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-xs leading-5 text-gray-600">
              {managedContent.policy_notice || usage?.policy_notice || 'AI Studio is for lawful artist growth content only. Do not submit abusive, sexual, hateful, illegal, impersonation, or private personal content. Unsafe requests may be blocked and reviewed by KalaManch admin.'}
            </div>

            <div className="mt-5">
              <div className="flex gap-2 overflow-x-auto pb-2">
                {toolGroups.map((group) => (
                  <button
                    key={group.title}
                    type="button"
                    onClick={() => {
                      setSelectedGroup(group.title);
                      const firstTool = tools.find((item) => group.ids.includes(item.id) && toolSettings[item.id]?.enabled !== false)
                        || tools.find((item) => group.ids.includes(item.id));
                      if (firstTool) chooseTool(firstTool);
                    }}
                    className={`shrink-0 rounded-full border px-4 py-2 text-sm font-extrabold transition ${
                      selectedGroup === group.title
                        ? 'border-[#FF6F00] bg-orange-50 text-[#E65100]'
                        : 'border-gray-200 bg-white text-slate-700 hover:border-orange-200'
                    }`}
                  >
                    {group.title}
                  </button>
                ))}
              </div>
              <div className="mt-2 grid max-h-[27rem] grid-cols-1 gap-2 overflow-y-auto pr-1">
                {visibleTools.map((tool) => {
                  const setting = toolSettings[tool.id] || {};
                  const disabledByAdmin = setting.enabled === false;
                  return (
                    <button
                      key={tool.id}
                      type="button"
                      disabled={disabledByAdmin}
                      onClick={() => chooseTool(tool)}
                      className={`rounded-2xl border px-3 py-2.5 text-left transition disabled:cursor-not-allowed ${
                        disabledByAdmin
                          ? 'border-gray-200 bg-gray-50 opacity-60'
                          : selectedTool.id === tool.id
                            ? 'border-[#FF6F00] bg-orange-50 shadow-sm'
                            : 'border-gray-200 bg-white hover:border-orange-200 hover:bg-orange-50/50'
                      }`}
                    >
                      <span className="flex items-start justify-between gap-2">
                        <span className="block text-sm font-extrabold leading-5 text-slate-950">{tool.title}</span>
                        <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-black uppercase tracking-wide ${disabledByAdmin ? 'bg-gray-200 text-gray-600' : 'bg-emerald-50 text-emerald-700'}`}>
                          {disabledByAdmin ? 'Paused' : `${setting.credit_cost || 1} cr`}
                        </span>
                      </span>
                      <span className="mt-1 line-clamp-2 block text-xs font-semibold leading-5 text-slate-700">{tool.subtitle}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div ref={formRef} className="scroll-mt-28 rounded-3xl border border-orange-100 bg-white p-5 shadow-xl sm:p-8">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#4A148C]">Write</p>
                <h2 className="mt-2 text-2xl font-bold text-gray-950">{selectedTool.title}</h2>
              </div>
              <span className="w-fit rounded-full bg-teal-50 px-3 py-1 text-xs font-bold text-[#0F766E]">
                {selectedToolEnabled ? `${selectedToolAccess.replace('_', ' ')} · ${selectedToolCreditCost} credit${selectedToolCreditCost > 1 ? 's' : ''}` : 'Paused by admin'}
              </span>
            </div>

            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <input
                value={artistName}
                onChange={(event) => setArtistName(event.target.value)}
                className="w-full rounded-2xl border border-gray-200 px-4 py-3 text-base outline-none focus:border-[#FF6F00] focus:ring-2 focus:ring-orange-100"
                placeholder={selectedTool.nameLabel || 'Artist name'}
              />
              <input
                value={artForm}
                onChange={(event) => setArtForm(event.target.value)}
                className="w-full rounded-2xl border border-gray-200 px-4 py-3 text-base outline-none focus:border-[#FF6F00] focus:ring-2 focus:ring-orange-100"
                placeholder={selectedTool.artFormLabel || 'Art form'}
              />
              <select
                value={tone}
                onChange={(event) => setTone(event.target.value)}
                className="w-full rounded-2xl border border-gray-200 px-4 py-3 text-base outline-none focus:border-[#FF6F00] focus:ring-2 focus:ring-orange-100"
              >
                <option>Professional</option>
                <option>Emotional</option>
                <option>Viral</option>
                <option>Hinglish</option>
              </select>
              <select
                value={language}
                onChange={(event) => setLanguage(event.target.value)}
                className="w-full rounded-2xl border border-gray-200 px-4 py-3 text-base outline-none focus:border-[#FF6F00] focus:ring-2 focus:ring-orange-100"
              >
                {languageOptions.map((option) => (
                  <option key={option}>{option}</option>
                ))}
              </select>
              <select
                value={purpose}
                onChange={(event) => setPurpose(event.target.value)}
                className="w-full rounded-2xl border border-gray-200 px-4 py-3 text-base outline-none focus:border-[#FF6F00] focus:ring-2 focus:ring-orange-100 sm:col-span-2"
              >
                {purposeOptions.map((option) => (
                  <option key={option}>{option}</option>
                ))}
              </select>
            </div>

            <div className="mt-4 rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm leading-6 text-emerald-900">
              Indian language mode supports Hindi captions, Urdu shayari formatting, Bhojpuri folk content, Punjabi stage announcements, Bengali cultural poetry, Marathi announcements, and other regional creator use cases.
            </div>

            <textarea
              value={details}
              onChange={(event) => setDetails(event.target.value)}
              className="mt-4 min-h-[160px] w-full rounded-2xl border border-gray-200 px-4 py-3 text-base outline-none focus:border-[#FF6F00] focus:ring-2 focus:ring-orange-100"
              placeholder={selectedTool.placeholder}
            />

            {error && (
              <div className="mt-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
                {error}
              </div>
            )}

            {!authLoading && !isAuthenticated && (
              <div className="mt-5 rounded-2xl border border-purple-100 bg-purple-50 px-4 py-4">
                <p className="text-sm font-semibold text-gray-700">
                  AI Studio is available only for registered and admin-verified KalaManch artists and collaborators.
                </p>
                <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                  <Link
                    to="/my-account"
                    className="rounded-full bg-[#FF6F00] px-5 py-3 text-center text-sm font-bold text-white transition hover:bg-[#E65100]"
                  >
                    Artist Login
                  </Link>
                  <Link
                    to="/collaborator/account"
                    className="rounded-full border-2 border-[#4A148C] px-5 py-3 text-center text-sm font-bold text-[#4A148C] transition hover:bg-purple-100"
                  >
                    Collaborator Login
                  </Link>
                </div>
              </div>
            )}

            {!authLoading && isAuthenticated && !isAdminVerifiedUser && (
              <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4">
                <p className="text-sm font-semibold text-amber-800">
                  {aiVerificationMessage}
                </p>
              </div>
            )}

            <div className="mt-6 grid gap-3 sm:flex">
              <button
                type="button"
                onClick={generate}
                disabled={Boolean(loading) || authLoading || !isAuthenticated || !isAdminVerifiedUser || !hasUsage || !selectedToolEnabled}
                className="rounded-full bg-[#FF6F00] px-6 py-3 text-sm font-bold text-white shadow-lg transition hover:bg-[#E65100] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading === selectedTool.id ? 'Generating...' : selectedTool.button}
              </button>
              <button
                type="button"
                onClick={copyResult}
                disabled={!currentResult}
                className="rounded-full border-2 border-[#FF6F00] px-6 py-3 text-sm font-bold text-[#FF6F00] transition hover:bg-orange-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {copiedTool === selectedTool.id ? 'Copied' : 'Copy Result'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setDetails('');
                  setResults((current) => {
                    const next = { ...current };
                    delete next[selectedTool.id];
                    return next;
                  });
                  setOpportunityResults((current) => {
                    const next = { ...current };
                    delete next[selectedTool.id];
                    return next;
                  });
                  setTranslatedResults((current) => {
                    const next = { ...current };
                    delete next[selectedTool.id];
                    return next;
                  });
                  setCopiedTool('');
                }}
                className="rounded-full border border-slate-200 px-6 py-3 text-sm font-bold text-slate-600 transition hover:bg-slate-50"
              >
                Clear
              </button>
            </div>

            {currentResult && (
              <article className="mt-8 rounded-3xl border border-purple-100 bg-purple-50/40 p-5">
                {showRawResult && (
                  <>
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                      <div>
                        <h3 className="text-lg font-bold text-gray-950">
                          {currentTranslation ? `Result in ${currentTranslation.language}` : 'Result'}
                        </h3>
                        <p className="mt-1 text-xs font-semibold text-slate-500">
                          Cleaned for reading. Copy still keeps the generated content.
                        </p>
                      </div>
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                        <select
                          value={translationLanguage}
                          onChange={(event) => setTranslationLanguage(event.target.value)}
                          className="rounded-full border border-purple-100 bg-white px-4 py-2 text-sm font-bold text-slate-700 outline-none focus:border-[#FF6F00]"
                        >
                          {languageOptions.filter((option) => option !== 'English + Hindi' && option !== 'Hinglish').map((option) => (
                            <option key={option}>{option}</option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={translateResult}
                          disabled={Boolean(loading) || !currentResult}
                          className="rounded-full border border-[#4A148C] bg-white px-4 py-2 text-sm font-bold text-[#4A148C] transition hover:bg-purple-50 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {loading === `${selectedTool.id}-translate` ? 'Translating...' : 'Translate Result'}
                        </button>
                        {currentTranslation && (
                          <button
                            type="button"
                            onClick={() => {
                              setTranslatedResults((current) => {
                                const next = { ...current };
                                delete next[selectedTool.id];
                                return next;
                              });
                            }}
                            className="rounded-full bg-white px-4 py-2 text-sm font-bold text-slate-600"
                          >
                            Original
                          </button>
                        )}
                      </div>
                    </div>
                    <ResultBlock text={displayedResult} />
                  </>
                )}
                {selectedTool.id === 'event-finder' && (
                  <div className="mt-7 rounded-3xl border border-orange-100 bg-white p-4">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                      <div>
                        <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#FF6F00]">KalaManch matches</p>
                        <h4 className="mt-1 text-xl font-black text-slate-950">Artists matching this event</h4>
                      </div>
                      <Link to="/artists" className="text-sm font-bold text-[#FF6F00] hover:text-[#E65100]">
                        Browse all artists
                      </Link>
                    </div>
                    {matchedArtists.length > 0 ? (
                      <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        {matchedArtists.map((match) => (
                          <RegisteredArtistCard key={match.id} artist={match} />
                        ))}
                      </div>
                    ) : (
                      <p className="mt-4 rounded-2xl bg-orange-50 px-4 py-3 text-sm font-semibold text-orange-900">
                        No direct artist matches found yet. Add art forms, city, or keywords like singer, anchor, tabla, poet, dancer.
                      </p>
                    )}
                  </div>
                )}
                {selectedTool.id === 'social-post-pack' && (
                  <div className={`${showRawResult ? 'mt-7' : ''} rounded-3xl border border-orange-100 bg-white p-4 sm:p-5`}>
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                      <div>
                        <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#FF6F00]">Ready to share</p>
                        <h4 className="mt-1 text-xl font-black text-slate-950">Platform post cards</h4>
                        <p className="mt-1 text-sm font-semibold text-slate-600">
                          Review, copy, and share manually. KalaManch does not auto-post without your action.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          copyTextToClipboard(currentResult);
                          setCopiedTool(`${selectedTool.id}-all`);
                        }}
                        className="w-fit rounded-full bg-emerald-50 px-4 py-2 text-xs font-black text-emerald-700 transition hover:bg-emerald-100"
                      >
                        {copiedTool === `${selectedTool.id}-all` ? 'Copied all' : 'Copy all'}
                      </button>
                    </div>
                    <div className="mt-4 grid gap-4 xl:grid-cols-2">
                      {socialSharePlatforms.map((platform) => {
                        const sectionText = socialCardText(currentResult, platform.key);
                        const shareHref = platform.key === 'X'
                          ? `https://x.com/compose/post?text=${encodeURIComponent(sectionText.slice(0, 260))}`
                          : platform.url;
                        return (
                          <article key={platform.key} className="rounded-3xl border border-slate-100 bg-slate-50 p-4 shadow-sm">
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex min-w-0 items-center gap-3">
                                <span className={`inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br ${platform.color} text-sm font-black text-white shadow`}>
                                  {platform.icon}
                                </span>
                                <div className="min-w-0">
                                  <h5 className="text-base font-black text-slate-950">{platform.label}</h5>
                                  <p className="text-xs font-bold text-slate-500">Ready-to-share copy</p>
                                </div>
                              </div>
                              <a href={shareHref} target="_blank" rel="noreferrer" className="shrink-0 rounded-full bg-white px-3 py-1.5 text-xs font-bold text-[#FF6F00] shadow-sm">
                                Open
                              </a>
                            </div>
                            <div className="mt-4 max-h-56 overflow-y-auto rounded-2xl border border-white bg-white p-3">
                              <p className="whitespace-pre-wrap break-words text-sm leading-6 text-slate-700">{sectionText}</p>
                            </div>
                            <div className="mt-4 grid grid-cols-2 gap-2">
                              <button
                                type="button"
                                onClick={() => {
                                  copyTextToClipboard(sectionText);
                                  setCopiedTool(`${selectedTool.id}-${platform.key}`);
                                }}
                                className="rounded-full border border-orange-200 px-4 py-2 text-xs font-bold text-[#FF6F00] hover:bg-orange-50"
                              >
                                {copiedTool === `${selectedTool.id}-${platform.key}` ? 'Copied' : 'Copy'}
                              </button>
                              <button
                                type="button"
                                onClick={async () => {
                                  await shareSocialText(platform, sectionText);
                                  setCopiedTool(`${selectedTool.id}-${platform.key}-share`);
                                }}
                                className="rounded-full bg-[#FF6F00] px-4 py-2 text-xs font-bold text-white hover:bg-[#E65100]"
                              >
                                {copiedTool === `${selectedTool.id}-${platform.key}-share` ? 'Shared' : 'Share'}
                              </button>
                            </div>
                          </article>
                        );
                      })}
                    </div>
                    <div className="mt-4 rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-800">
                      Content is checked before generation. Please review names, dates, links, and claims before posting on any social platform.
                    </div>
                  </div>
                )}
                {selectedTool.id === 'events-near-me' && currentOpportunities && (
                  <div className={`${showRawResult ? 'mt-7' : ''} rounded-3xl border border-emerald-100 bg-white p-4`}>
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                      <div>
                        <p className="text-xs font-bold uppercase tracking-[0.22em] text-emerald-700">Free event opportunity search</p>
                        <h4 className="mt-1 text-xl font-black text-slate-950">Venue and search leads</h4>
                      </div>
                      <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-700">
                        {currentOpportunities.events?.length > 0 ? 'Live results' : 'Free search'}
                      </span>
                    </div>
                    {currentOpportunities.events?.length > 0 && (
                      <div className="mt-4 grid gap-3 sm:grid-cols-2">
                        {currentOpportunities.events.slice(0, 8).map((event) => (
                          <article key={event.id || `${event.title}-${event.date}`} className="rounded-2xl border border-purple-100 bg-purple-50/60 p-4">
                            <p className="text-xs font-bold uppercase tracking-[0.16em] text-purple-700">{publicEventSourceLabel(event.source)}</p>
                            <h5 className="mt-2 text-base font-black text-slate-950">{event.title}</h5>
                            {[event.date, event.venue, event.address].filter(Boolean).length > 0 && (
                              <p className="mt-2 text-sm leading-6 text-slate-600">{[event.date, event.venue, event.address].filter(Boolean).join(' | ')}</p>
                            )}
                            {event.url && (
                              <a href={event.url} target="_blank" rel="noreferrer" className="mt-3 inline-flex rounded-full bg-white px-3 py-1 text-xs font-bold text-purple-700">
                                Open event
                              </a>
                            )}
                          </article>
                        ))}
                      </div>
                    )}
                    {!currentOpportunities.events?.length && !currentOpportunities.venues?.length && (
                      <p className="mt-4 rounded-2xl bg-orange-50 px-4 py-3 text-sm font-semibold text-orange-900">
                        No live listings returned for this exact search yet. Use the public links below, or try a broader search like music events in Mumbai.
                      </p>
                    )}
                    {currentOpportunities.venues?.length > 0 && (
                      <div className="mt-4 grid gap-3 sm:grid-cols-2">
                        {currentOpportunities.venues.slice(0, 8).map((venue) => (
                          <article key={venue.id || venue.title} className="rounded-2xl border border-emerald-100 bg-emerald-50/50 p-4">
                            <p className="text-xs font-bold uppercase tracking-[0.16em] text-emerald-700">{venue.type || 'Venue'}</p>
                            <h5 className="mt-2 text-base font-black text-slate-950">{venue.title}</h5>
                            {venue.address && <p className="mt-2 text-sm leading-6 text-slate-600">{venue.address}</p>}
                            <div className="mt-3 flex flex-wrap gap-2">
                              {venue.phone && <span className="rounded-full bg-white px-3 py-1 text-xs font-bold text-slate-700">{venue.phone}</span>}
                              {venue.url && (
                                <a href={venue.url} target="_blank" rel="noreferrer" className="rounded-full bg-white px-3 py-1 text-xs font-bold text-emerald-700">
                                  Website
                                </a>
                              )}
                            </div>
                          </article>
                        ))}
                      </div>
                    )}
                    {currentOpportunities.search_links?.length > 0 && (
                      <div className="mt-5 flex flex-wrap gap-2">
                        {currentOpportunities.search_links.map((link) => (
                          <a key={link.url} href={link.url} target="_blank" rel="noreferrer" className="rounded-full border border-orange-200 px-4 py-2 text-sm font-bold text-[#FF6F00] hover:bg-orange-50">
                            {link.label}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </article>
            )}
          </div>
        </div>
      </section>
    </main>
  );
}
